import { useEffect, useState } from "react";
import { Lightning, Sparkle, Target, Trophy, CurrencyDollar, ArrowRight, Compass, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { demoStatus, demoSeed } from "../lib/api";

const STORAGE_KEY = "adforge_welcome_dismissed_v1";

const FEATURES = [
  {
    icon: Sparkle,
    color: "#ccff00",
    title: "Drop a URL → get 6 platforms",
    desc: "Auto-generate Google, Meta, TikTok, Instagram, X, Reddit ads in <10s.",
  },
  {
    icon: Target,
    color: "#7dd3fc",
    title: "A/B every variant automatically",
    desc: "3 headlines × platform. Chi-square significance built-in.",
  },
  {
    icon: Trophy,
    color: "#f472b6",
    title: "Rank by clicks, CVR or revenue",
    desc: "Winner auto-lock switches metric — scale your best sellers, not just clickers.",
  },
  {
    icon: CurrencyDollar,
    color: "#a78bfa",
    title: "Track sales across currencies",
    desc: "Pixel + S2S webhook + manual. FX-consolidated to USD automatically.",
  },
];

/**
 * First-run welcome screen — offers a one-click demo seed OR a fresh start.
 * Renders only when the workspace has no ad sets AND the user hasn't dismissed.
 */
export default function WelcomeDialog({ onDemoSeeded, onStartTour }) {
  const [open, setOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    // Only run the check once per session
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;
    demoStatus()
      .then((s) => {
        if (s.is_first_run) setOpen(true);
      })
      .catch(() => {});
  }, []);

  const dismiss = (permanent = true) => {
    if (permanent) localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const res = await demoSeed();
      toast.success(
        res.already_seeded
          ? "Demo data already loaded"
          : `Demo loaded · ${res.clicks_seeded} clicks + ${res.conversions_seeded} conversions`,
      );
      dismiss();
      onDemoSeeded?.(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load demo");
    } finally {
      setSeeding(false);
    }
  };

  const startTour = () => {
    dismiss();
    onStartTour?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss(false)}>
      <DialogContent
        className="bg-[#050505] border border-white/10 rounded-none max-w-2xl p-0 overflow-hidden"
        data-testid="welcome-dialog"
      >
        <VisuallyHidden>
          <DialogTitle>Welcome to AdForge</DialogTitle>
          <DialogDescription>
            Load a demo dataset or take a guided tour of the app.
          </DialogDescription>
        </VisuallyHidden>
        {/* Header banner */}
        <div className="bg-gradient-to-br from-[#ccff00]/20 via-transparent to-pink-500/10 border-b border-white/10 px-8 pt-8 pb-6 relative">
          <button
            onClick={() => dismiss(false)}
            data-testid="welcome-close"
            className="absolute top-4 right-4 p-1 text-zinc-500 hover:text-white transition-colors"
            aria-label="close"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-[#ccff00] flex items-center justify-center text-black">
              <Lightning size={24} weight="fill" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              welcome · v11
            </div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-tight">
            Turn any URL into <span className="text-[#ccff00]">high-CTR ads</span>.
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            You&apos;re one click away from a working demo — real clicks, real conversions, real leaderboards.
          </p>
        </div>

        {/* Feature grid */}
        <div className="px-8 py-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="bg-[#0a0a0a] border border-white/5 p-3 hover:border-white/15 transition-colors"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <f.icon size={14} weight="fill" style={{ color: f.color }} />
                <div className="font-mono text-[10px] uppercase tracking-wider text-white font-bold">
                  {f.title}
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 leading-relaxed">
                {f.desc}
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-8 pb-6 pt-2 flex flex-col sm:flex-row gap-2">
          <button
            data-testid="welcome-seed-btn"
            onClick={seed}
            disabled={seeding}
            className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-3 px-4 flex items-center justify-center gap-2 transition-colors group"
          >
            {seeding ? (
              <>
                <Sparkle size={14} weight="fill" className="animate-spin" />
                loading demo…
              </>
            ) : (
              <>
                Load demo dataset
                <ArrowRight
                  size={14}
                  weight="bold"
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </>
            )}
          </button>
          <button
            data-testid="welcome-tour-btn"
            onClick={startTour}
            className="border border-white/10 hover:border-white/30 text-zinc-300 hover:text-white py-3 px-4 flex items-center justify-center gap-2 transition-colors"
          >
            <Compass size={14} />
            Take a 60-sec tour
          </button>
        </div>
        <div className="px-8 pb-6 text-center">
          <button
            data-testid="welcome-skip-btn"
            onClick={() => dismiss(true)}
            className="text-[10px] font-mono text-zinc-600 hover:text-zinc-400 uppercase tracking-widest"
          >
            skip · I&apos;ll drop my own URL
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
